import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckSquare, Users, FolderKanban, UserCheck, Receipt, Landmark, Boxes, Check, ArrowRight, Loader2, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { useTabTitle } from "@/lib/tabs";
import { toast } from "sonner";

const ICONS = {
  "check-square": CheckSquare,
  "users": Users,
  "folder-kanban": FolderKanban,
  "user-check": UserCheck,
  "receipt": Receipt,
  "landmark": Landmark,
};

export default function ModulesPage() {
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // module key currently enabling

  useTabTitle("Modules", "boxes");

  const load = async () => {
    try {
      const r = await api.get("/modules");
      setModules(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const enable = async (m) => {
    setBusy(m.key);
    try {
      await api.post(`/modules/${m.key}/enable`);
      toast.success(`${m.name} enabled`);
      await load();
      window.dispatchEvent(new Event("ubos:collections-changed")); // refresh sidebar
      await openModule({ ...m, state: "active" });
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const openModule = async (m) => {
    // Jump to the module's home collection's records (its default view first).
    if (!m.home_entity_type) { nav("/entity-types"); return; }
    try {
      const r = await api.get("/entity-types");
      const et = (r.data || []).find((e) => e.key === m.home_entity_type);
      nav(et ? `/entity-types/${et.id}/records` : "/entity-types");
    } catch {
      nav("/entity-types");
    }
  };

  const active = modules.filter((m) => m.state === "active");
  const [syncing, setSyncing] = useState(false);

  const syncSchema = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/modules/sync");
      const t = r.data?.totals || {};
      const added = (t.entity_types || 0) + (t.field_definitions || 0) + (t.relationship_definitions || 0);
      toast.success(added > 0
        ? `Workspace updated — added ${t.entity_types || 0} collections, ${t.field_definitions || 0} fields.`
        : "Your workspace is already up to date.");
      await load();
      window.dispatchEvent(new Event("ubos:collections-changed"));
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSyncing(false); }
  };

  return (
    <>
      <PageHeader
        title="Modules"
        subtitle="Turn on the parts of UBOS your business needs. Each module is a ready-to-use app — enable it and it appears in your workspace."
        breadcrumbs={[{ label: "UBOS" }, { label: "Modules" }]}
        actions={active.length > 0 && (
          <Button variant="outline" onClick={syncSchema} disabled={syncing} data-testid="sync-schema-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Updating…" : "Update workspace"}
          </Button>
        )}
      />
      <PageBody>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="modules-grid">
            {modules.map((m) => {
              const Icon = ICONS[m.icon] || Boxes;
              const isActive = m.state === "active";
              const comingSoon = m.status === "coming_soon";
              return (
                <Card
                  key={m.key}
                  className={`flex flex-col ${comingSoon ? "opacity-70" : ""}`}
                  data-testid={`module-card-${m.key}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                          <Check className="w-3 h-3" /> Enabled
                        </span>
                      ) : comingSoon ? (
                        <span className="text-[11px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
                          Coming soon
                        </span>
                      ) : (
                        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                          {m.category}
                        </span>
                      )}
                    </div>
                    <CardTitle className="mt-3">{m.name}</CardTitle>
                    <CardDescription>{m.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1" />
                  <CardFooter>
                    {comingSoon ? (
                      <Button variant="outline" disabled className="w-full">Coming soon</Button>
                    ) : isActive ? (
                      <Button variant="outline" className="w-full" onClick={() => openModule(m)} data-testid={`module-open-${m.key}`}>
                        Open <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    ) : (
                      <Button className="w-full" onClick={() => enable(m)} disabled={busy === m.key} data-testid={`module-enable-${m.key}`}>
                        {busy === m.key ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enabling…</>) : "Enable"}
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}
        {!loading && active.length === 0 && (
          <p className="mt-6 text-sm text-muted-foreground">
            Nothing enabled yet — start with <b>Tasks</b> to see how a module lights up your workspace.
          </p>
        )}
      </PageBody>
    </>
  );
}
